import { Box, Typography, Button, Divider, Chip, Collapse } from "@mui/material";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import { useState } from "react";
import { usePanelTokens } from "../../../../utils/panelTokens";

const DocStepCard = ({ step, isActive, onSeekVideo }) => {
  const T = usePanelTokens();
  const [tipsOpen, setTipsOpen] = useState(false);
  const [imgZoomed, setImgZoomed] = useState(false);

  const formatTimestamp = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, "0")}`;
  };

  return (
    <>
      <Box
        id={step.id}
        sx={{
          mb: 3,
          borderRadius: 2.5,
          overflow: "hidden",
          border: `1px solid ${isActive ? "rgba(58,142,255,0.5)" : T.border}`,
          backgroundColor: T.surface,
          boxShadow: isActive
            ? "0 0 0 1px rgba(58,142,255,0.18), 0 8px 32px rgba(58,142,255,0.1)"
            : "none",
          transition: "border-color 0.3s, box-shadow 0.3s",
          scrollMarginTop: 24,
          position: "relative",
        }}
      >
        {/* Active left accent */}
        <Box
          sx={{
            position: "absolute",
            left: 0,
            top: 0,
            bottom: 0,
            width: 3,
            borderRadius: "2.5px 0 0 2.5px",
            background: isActive
              ? `linear-gradient(to bottom, ${T.accent}, ${T.accent}66)`
              : "transparent",
            transition: "background 0.3s",
          }}
        />

        {/* Header */}
        <Box
          sx={{
            px: 3,
            py: 2,
            display: "flex",
            alignItems: "center",
            gap: 2,
            borderBottom: `1px solid ${T.borderSoft}`,
            backgroundColor: isActive ? "rgba(58,142,255,0.06)" : "transparent",
          }}
        >
          {/* Step number badge */}
          <Box
            sx={{
              minWidth: 36,
              height: 36,
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              background: isActive
                ? `linear-gradient(135deg, ${T.accent}, ${T.accentHover})`
                : T.track,
              border: `1.5px solid ${isActive ? "rgba(58,142,255,0.5)" : T.border}`,
              boxShadow: isActive ? "0 0 12px rgba(58,142,255,0.35)" : "none",
            }}
          >
            <Typography
              sx={{
                fontSize: 12,
                fontWeight: 700,
                color: isActive ? "#fff" : T.textMuted,
                lineHeight: 1,
              }}
            >
              {String(step.stepNumber).padStart(2, "0")}
            </Typography>
          </Box>

          {/* Title */}
          <Typography
            variant="h6"
            sx={{
              fontSize: 15,
              fontWeight: 600,
              color: isActive ? T.accent : T.text,
              flex: 1,
              letterSpacing: "-0.01em",
            }}
          >
            {step.title}
          </Typography>

          {/* Timestamp chip */}
          <Chip
            label={formatTimestamp(step.videoTimestamp)}
            size="small"
            sx={{
              fontSize: 11,
              height: 22,
              fontFamily: "monospace",
              fontWeight: 600,
              letterSpacing: "0.04em",
              backgroundColor: "rgba(58,142,255,0.12)",
              color: T.accent,
              border: "1px solid rgba(58,142,255,0.2)",
            }}
          />
        </Box>

        {/* Body */}
        <Box sx={{ px: 3, py: 2.5 }}>
          <Typography
            sx={{
              fontSize: 14,
              color: T.textMuted,
              lineHeight: 1.75,
              mb: 2.5,
            }}
          >
            {step.description}
          </Typography>

          {/* Screenshot */}
          <Box
            sx={{
              position: "relative",
              borderRadius: 2,
              overflow: "hidden",
              border: `1px solid ${T.border}`,
              mb: 2.5,
              backgroundColor: T.surfaceAlt,
              cursor: "zoom-in",
              "&:hover .zoom-overlay": { opacity: 1 },
              "&:hover img": { transform: "scale(1.01)" },
            }}
            onClick={() => setImgZoomed(true)}
          >
            <img
              src={step.imageUrl}
              alt={`Step ${step.stepNumber}: ${step.title}`}
              style={{
                width: "100%",
                display: "block",
                objectFit: "cover",
                transition: "transform 0.3s ease",
              }}
              onError={(e) => {
                e.target.parentNode.style.minHeight = "120px";
                e.target.style.display = "none";
              }}
            />
            <Box
              className="zoom-overlay"
              sx={{
                position: "absolute",
                inset: 0,
                backgroundColor: "rgba(0,0,0,0.35)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                opacity: 0,
                transition: "opacity 0.2s",
              }}
            >
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: "50%",
                  backgroundColor: "rgba(255,255,255,0.12)",
                  backdropFilter: "blur(8px)",
                  border: "1px solid rgba(255,255,255,0.25)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <ZoomInIcon sx={{ color: "#fff", fontSize: 20 }} />
              </Box>
            </Box>
          </Box>

          {/* Jump-to-video button */}
          <Button
            startIcon={<PlayCircleOutlineIcon sx={{ fontSize: "18px !important" }} />}
            onClick={() => onSeekVideo(step.videoTimestamp, step.title)}
            variant="outlined"
            size="small"
            sx={{
              fontSize: 13,
              fontWeight: 500,
              textTransform: "none",
              borderRadius: 1.5,
              px: 2,
              py: 0.75,
              borderColor: "rgba(58,142,255,0.3)",
              color: T.accent,
              mb: step.tips?.length ? 2.5 : 0,
              "&:hover": {
                borderColor: T.accent,
                backgroundColor: "rgba(58,142,255,0.07)",
              },
            }}
          >
            Watch this step at {formatTimestamp(step.videoTimestamp)}
          </Button>

          {/* Tips */}
          {step.tips?.length > 0 && (
            <>
              <Divider sx={{ mb: 2, borderColor: T.borderSoft }} />
              <Box
                sx={{
                  borderRadius: 2,
                  overflow: "hidden",
                  border: "1px solid rgba(234,179,8,0.2)",
                  backgroundColor: "rgba(234,179,8,0.05)",
                }}
              >
                <Box
                  onClick={() => setTipsOpen(!tipsOpen)}
                  sx={{
                    px: 2,
                    py: 1.25,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    cursor: "pointer",
                    "&:hover": { backgroundColor: "rgba(234,179,8,0.04)" },
                  }}
                >
                  <LightbulbOutlinedIcon sx={{ fontSize: 15, color: T.warning }} />
                  <Typography
                    sx={{
                      fontSize: 12,
                      fontWeight: 700,
                      color: T.warning,
                      textTransform: "uppercase",
                      letterSpacing: "0.08em",
                      flex: 1,
                    }}
                  >
                    Tips ({step.tips.length})
                  </Typography>
                  {tipsOpen ? (
                    <ExpandLessIcon sx={{ fontSize: 16, color: T.warning }} />
                  ) : (
                    <ExpandMoreIcon sx={{ fontSize: 16, color: T.warning }} />
                  )}
                </Box>

                <Collapse in={tipsOpen}>
                  <Box
                    sx={{
                      px: 2,
                      pb: 1.5,
                      borderTop: "1px solid rgba(234,179,8,0.12)",
                      pt: 1.25,
                      display: "flex",
                      flexDirection: "column",
                      gap: 1,
                    }}
                  >
                    {step.tips.map((tip, i) => (
                      <Box
                        key={i}
                        sx={{ display: "flex", gap: 1.25, alignItems: "flex-start" }}
                      >
                        <Box
                          sx={{
                            width: 5,
                            height: 5,
                            borderRadius: "50%",
                            backgroundColor: T.warning,
                            flexShrink: 0,
                            mt: 0.75,
                          }}
                        />
                        <Typography
                          sx={{ fontSize: 13, color: T.textMuted, lineHeight: 1.65 }}
                        >
                          {tip}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Collapse>
              </Box>
            </>
          )}
        </Box>
      </Box>

      {/* Zoomed image modal */}
      {imgZoomed && (
        <Box
          onClick={() => setImgZoomed(false)}
          sx={{
            position: "fixed",
            inset: 0,
            zIndex: 1400,
            backgroundColor: "rgba(0,0,0,0.9)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "zoom-out",
            p: 3,
          }}
        >
          <img
            src={step.imageUrl}
            alt={step.title}
            style={{
              maxWidth: "90vw",
              maxHeight: "88vh",
              borderRadius: 12,
              objectFit: "contain",
              boxShadow: "0 24px 80px rgba(0,0,0,0.7)",
            }}
          />
        </Box>
      )}
    </>
  );
};

export default DocStepCard;
